import React, { useState, useCallback, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  Stack,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { TableKit, toHebrewNumeral } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import ImportSection from '../../components/ImportSection.jsx';
import ExamCard from './ExamCard.jsx';
import { formatHebrewDate } from '../../utils/hebrewDate';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { useQuickCreate } from '../../next/quickCreate';

const TYPE_LABELS = { time: 'זמן', shimur: 'שימור', miktzoa: 'מקצוע' };
const STATUS_LABELS = { draft: 'טיוטה', open: 'פתוח', closed: 'סגור', graded: 'צויין' };
const STATUS_COLORS = { draft: 'default', open: 'success', closed: 'warning', graded: 'info' };

const periodValue = (periodId) => {
  if (!periodId) return '';
  if (typeof periodId === 'object') return periodId._id || '';
  return periodId;
};

const periodName = (periodId, periodsList = []) => {
  if (!periodId) return '';
  if (typeof periodId === 'object') return String(periodId.name || '').trim();
  const match = periodsList.find((p) => String(p._id) === String(periodId));
  return String(match?.name || '').trim();
};

/** טווח סימנים מ-libraryLinks באותיות עבריות (ללא נושא). */
const formatSimanimRanges = (libraryLinks) => {
  const links = Array.isArray(libraryLinks) ? libraryLinks : [];
  const linkParts = [];
  for (const link of links) {
    const chelek = String(link?.chelek || '').trim();
    const ranges = (link?.ranges || [])
      .map((r) => {
        const from = r?.simanFrom;
        const to = r?.simanTo;
        if (from == null || to == null || from === '' || to === '') return '';
        const fromNum = Number(from);
        const toNum = Number(to);
        if (!fromNum || !toNum) return '';
        const fromHeb = toHebrewNumeral(fromNum);
        const toHeb = toHebrewNumeral(toNum);
        return fromNum === toNum ? fromHeb : `${fromHeb}-${toHeb}`;
      })
      .filter(Boolean);
    if (!chelek && ranges.length === 0) continue;
    if (ranges.length === 0) {
      linkParts.push(chelek);
    } else {
      linkParts.push(`${chelek} סי' ${ranges.join(', ')}`.trim());
    }
  }
  return linkParts.join('; ');
};

const ExamsList = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  useQuickCreate(() => setSelectedId('new'));
  const [periods, setPeriods] = useState([]);
  const [importExamPeriodId, setImportExamPeriodId] = useState('');
  const [importGradePeriodId, setImportGradePeriodId] = useState('');
  const [periodSavingId, setPeriodSavingId] = useState(null);

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportPeriodIds, setExportPeriodIds] = useState([]);
  const [exportMode, setExportMode] = useState('single');
  const [exportOrientation, setExportOrientation] = useState('landscape');
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    api.get('/exam-periods').then(({ data }) => {
      const list = data.data || [];
      setPeriods(list);
      const active = list.find((p) => p.isActive);
      if (active) {
        setImportExamPeriodId(active._id);
        setImportGradePeriodId(active._id);
      }
    }).catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/exams', { params: { page: 1, pageSize: 100000 } });
      setAllData((res.data || []).map((exam) => ({
        ...exam,
        _periodName: periodName(exam.periodId, periods),
        _simanimRange: formatSimanimRanges(exam.libraryLinks),
      })));
    } catch (err) {
      console.error('שגיאה בשליפת מבחנים:', err);
    } finally {
      setLoading(false);
    }
  }, [periods]);

  useEffect(() => { loadData(); }, [loadData]);

  const openExportDialog = () => {
    const sorted = [...periods].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setExportPeriodIds(sorted.map((p) => String(p._id)));
    setExportMode('single');
    setExportDialogOpen(true);
  };

  const downloadBlob = (data, filename, mime) => {
    const url = URL.createObjectURL(new Blob([data], { type: mime }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const parseExportError = async (err, fallback) => {
    let message = fallback;
    try {
      const text = await err.response?.data?.text?.();
      if (text) {
        const parsed = JSON.parse(text);
        if (parsed?.error) message = parsed.error;
      }
    } catch { /* ignore */ }
    return message;
  };

  const handleDetailedExport = async () => {
    if (exportPeriodIds.length === 0) {
      alert('יש לבחור לפחות מועד אחד');
      return;
    }

    setExportLoading(true);
    try {
      const day = new Date().toISOString().slice(0, 10);
      const isZip = exportMode === 'byKolel' || exportMode === 'byCity';
      const response = await api.get('/exams/export-detailed', {
        params: {
          periodIds: exportPeriodIds.join(','),
          mode: exportMode,
        },
        responseType: 'blob',
      });

      const mime = isZip
        ? 'application/zip'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const filename = exportMode === 'byCity'
        ? `מבחנים_מפורט_לפי_עיר_${day}.zip`
        : exportMode === 'byKolel'
          ? `מבחנים_מפורט_לפי_כולל_${day}.zip`
          : exportMode === 'bySubject'
            ? `סיכום_חומר_לפי_מקצוע_${day}.xlsx`
            : `סיכום_חומר_${day}.xlsx`;

      downloadBlob(response.data, filename, mime);
      setExportDialogOpen(false);
    } catch (err) {
      console.error('שגיאה בייצוא:', err);
      alert(await parseExportError(err, 'שגיאה בייצוא'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleDetailedPdfExport = async () => {
    if (exportPeriodIds.length === 0) {
      alert('יש לבחור לפחות מועד אחד');
      return;
    }

    setExportLoading(true);
    try {
      const day = new Date().toISOString().slice(0, 10);
      const isZip = exportMode === 'byKolel' || exportMode === 'byCity';
      const response = await api.get('/exams/export-detailed-pdf', {
        params: {
          periodIds: exportPeriodIds.join(','),
          mode: exportMode,
          orientation: exportOrientation,
        },
        responseType: 'blob',
      });

      const mime = isZip ? 'application/zip' : 'application/pdf';
      const filename = exportMode === 'byCity'
        ? `מבחנים_מפורט_לפי_עיר_${day}.zip`
        : exportMode === 'byKolel'
          ? `מבחנים_מפורט_לפי_כולל_${day}.zip`
          : exportMode === 'bySubject'
            ? `סיכום_חומר_לפי_מקצוע_${day}.pdf`
            : `סיכום_חומר_${day}.pdf`;

      downloadBlob(response.data, filename, mime);
      setExportDialogOpen(false);
    } catch (err) {
      console.error('שגיאה בייצוא PDF:', err);
      alert(await parseExportError(err, 'שגיאה בייצוא PDF'));
    } finally {
      setExportLoading(false);
    }
  };

  const handlePeriodChange = async (row, nextPeriodId) => {
    const value = nextPeriodId || null;
    setPeriodSavingId(row._id);
    try {
      const { data } = await api.put(`/exams/${row._id}`, {
        periodId: value,
        version: row.version,
      });
      const populated = data.periodId && typeof data.periodId === 'object'
        ? data.periodId
        : (value ? periods.find((p) => p._id === value) || value : null);
      setAllData((prev) => prev.map((exam) => (
        exam._id === row._id
          ? {
            ...exam,
            ...data,
            periodId: populated,
            _periodName: periodName(populated, periods),
            version: data.version,
          }
          : exam
      )));
    } catch (err) {
      console.error('שגיאה בעדכון תקופה:', err);
      alert(err.response?.data?.error || 'שגיאה בעדכון תקופה');
    } finally {
      setPeriodSavingId(null);
    }
  };

  const columnsWithActions = [
    { key: 'code', title: 'קוד', sortable: true, filterable: true, minWidth: '80px' },
    { key: 'name', title: 'שם מבחן', sortable: true, filterable: true, minWidth: '140px' },
    {
      key: 'type', title: 'סוג', sortable: true, filterable: true, minWidth: '80px',
      render: (v) => TYPE_LABELS[v] || v,
    },
    { key: 'subject', title: 'נושא', sortable: true, filterable: true },
    {
      key: '_simanimRange',
      title: 'טווח סימנים',
      sortable: true,
      filterable: true,
      minWidth: '160px',
      render: (v) => v || '',
    },
    {
      key: 'date', title: 'תאריך', sortable: true, minWidth: '100px',
      render: (v) => formatHebrewDate(v),
    },
    {
      key: 'status', title: 'סטטוס', sortable: true, filterable: true, minWidth: '80px',
      render: (v) => <Chip label={STATUS_LABELS[v] || v} color={STATUS_COLORS[v] || 'default'} size="small" />,
    },
    {
      key: '_periodName',
      title: 'תקופה',
      sortable: true,
      filterable: true,
      minWidth: '160px',
      render: (_v, row) => (
        <TextField
          select
          size="small"
          fullWidth
          value={periodValue(row.periodId)}
          disabled={periodSavingId === row._id}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            handlePeriodChange(row, e.target.value);
          }}
        >
          <MenuItem value="">ללא תקופה</MenuItem>
          {periods.map((p) => (
            <MenuItem key={p._id} value={p._id}>
              {p.name}{p.isActive ? ' (פעיל)' : ''}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    { key: 'kolelCode', title: 'קוד כולל', sortable: true, filterable: true },
    { key: 'city', title: 'עיר', sortable: true, filterable: true, minWidth: '100px' },
    {
      key: 'introText',
      title: 'טקסט בתחילת המבחן',
      sortable: true,
      filterable: true,
      minWidth: '160px',
      render: (v) => {
        const text = (v || '').trim();
        if (!text) return '';
        const short = text.length > 60 ? `${text.slice(0, 60)}…` : text;
        return (
          <Tooltip title={text} placement="top" enterDelay={400}>
            <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
              {short}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: 'graderNotes',
      title: 'הערות והארות (לבודק)',
      sortable: true,
      filterable: true,
      minWidth: '160px',
      render: (v) => {
        const text = (v || '').trim();
        if (!text) return '';
        const short = text.length > 60 ? `${text.slice(0, 60)}…` : text;
        return (
          <Tooltip title={text} placement="top" enterDelay={400}>
            <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
              {short}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: '_actions', title: 'פעולות', sortable: false, filterable: false,
      render: (_, row) => (
        <Button size="small" variant="outlined" onClick={() => setSelectedId(row._id)}>
          ערוך
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <AdminPageHeader
        title="מבחנים"
        sub={!loading ? `${allData.length.toLocaleString('he-IL')} מבחנים` : undefined}
        primary={{ label: 'מבחן חדש', onClick: () => setSelectedId('new') }}
        menus={[{
          key: 'export',
          label: 'ייצוא',
          icon: 'download',
          items: [{ key: 'detailed', label: 'ייצוא מפורט', icon: 'download', onClick: openExportDialog, disabled: periods.length === 0 }],
        }]}
        legacy={(
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h5" fontWeight={700}>מבחנים</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={openExportDialog} disabled={periods.length === 0}>
                ייצוא מפורט
              </Button>
              <Button variant="contained" color="secondary" onClick={() => setSelectedId('new')}>
                + מבחן חדש
              </Button>
            </Box>
          </Box>
        )}
      />

      <Dialog
        open={exportDialogOpen}
        onClose={() => !exportLoading && setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ייצוא מפורט</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="exams-export-periods-label">תקופות</InputLabel>
              <Select
                labelId="exams-export-periods-label"
                label="תקופות"
                multiple
                value={exportPeriodIds}
                onChange={(e) => setExportPeriodIds(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value
                )}
                disabled={exportLoading}
                renderValue={(selected) => {
                  const labels = selected.map((id) => {
                    const p = periods.find((x) => String(x._id) === String(id));
                    return p?.name || id;
                  });
                  return labels.join(', ');
                }}
              >
                {periods.map((p) => (
                  <MenuItem key={p._id} value={String(p._id)}>
                    <Checkbox checked={exportPeriodIds.includes(String(p._id))} size="small" />
                    <ListItemText primary={`${p.name}${p.isActive ? ' (פעיל)' : ''}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => setExportPeriodIds(periods.map((p) => String(p._id)))}
                disabled={exportLoading}
              >
                הכל
              </Button>
              <Button
                size="small"
                onClick={() => setExportPeriodIds([])}
                disabled={exportLoading}
              >
                נקה
              </Button>
            </Box>

            <FormControl>
              <FormLabel>מצב ייצוא</FormLabel>
              <RadioGroup
                value={exportMode}
                onChange={(e) => setExportMode(e.target.value)}
              >
                <FormControlLabel
                  value="single"
                  control={<Radio size="small" />}
                  label="סיכום חומר לפי חלק (או״ח / יו״ד / אה״ע / חו״מ)"
                  disabled={exportLoading}
                />
                <FormControlLabel
                  value="bySubject"
                  control={<Radio size="small" />}
                  label="סיכום חומר לפי מקצוע"
                  disabled={exportLoading}
                />
                <FormControlLabel
                  value="byKolel"
                  control={<Radio size="small" />}
                  label="ZIP לפי כולל — קובץ נפרד לכל כולל"
                  disabled={exportLoading}
                />
                <FormControlLabel
                  value="byCity"
                  control={<Radio size="small" />}
                  label="ZIP לפי עיר — קובץ נפרד לכל עיר"
                  disabled={exportLoading}
                />
              </RadioGroup>
            </FormControl>

            <FormControl>
              <FormLabel>כיוון דף (PDF)</FormLabel>
              <RadioGroup
                row
                value={exportOrientation}
                onChange={(e) => setExportOrientation(e.target.value)}
              >
                <FormControlLabel
                  value="portrait"
                  control={<Radio size="small" />}
                  label="לאורך"
                  disabled={exportLoading}
                />
                <FormControlLabel
                  value="landscape"
                  control={<Radio size="small" />}
                  label="לרוחב"
                  disabled={exportLoading}
                />
              </RadioGroup>
            </FormControl>

            <Typography variant="body2" color="text.secondary">
              {exportMode === 'single' &&
                'קובץ אחד: ארבעת חלקי השו״ע וטווחי הסימנים בכל חלק — בלי כולל.'}
              {exportMode === 'bySubject' &&
                'קובץ אחד: חלק (או״ח→יו״ד→אה״ע→חו״מ ברצף), ובתוך כל חלק — מקצועות וטווחי סימנים.'}
              {(exportMode === 'byKolel' || exportMode === 'byCity') &&
                'ZIP לפי כולל/עיר: קוד/שם/עיר כולל, ולכל תקופה — קוד מבחן, שם מבחן וחומר נבחן. נכללים רק מבחנים עם קוד כולל.'}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={exportLoading}>
            ביטול
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleDetailedPdfExport}
            disabled={exportLoading || exportPeriodIds.length === 0}
            startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            ייצוא PDF
          </Button>
          <Button
            variant="contained"
            onClick={handleDetailedExport}
            disabled={exportLoading || exportPeriodIds.length === 0}
            startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {(exportMode === 'single' || exportMode === 'bySubject') ? 'ייצוא Excel' : 'ייצוא'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selectedId}
        onClose={() => { setSelectedId(null); loadData(); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <ExamCard
            recordId={selectedId}
            onClose={() => {
              setSelectedId(null);
              loadData();
            }}
          />
        </DialogContent>
      </Dialog>

      <TableKit
        columns={columnsWithActions}
        data={allData}
        total={allData.length}
        loading={loading}
        clientSideMode
        emptyMessage="לא נמצאו מבחנים"
        exportFileName="מבחנים"
        onOpenCard={(row) => setSelectedId(row._id)}
      />

      <Box sx={{ mt: 2, mb: 1, maxWidth: 280 }}>
        <TextField
          select
          size="small"
          fullWidth
          label="מועד לייבוא מבחנים"
          value={importExamPeriodId}
          onChange={(e) => setImportExamPeriodId(e.target.value)}
        >
          <MenuItem value="">ללא תקופה</MenuItem>
          {periods.map((p) => (
            <MenuItem key={p._id} value={p._id}>{p.name}{p.isActive ? ' (פעיל)' : ''}</MenuItem>
          ))}
        </TextField>
      </Box>

      <ImportSection
        label="מבחנים"
        endpoint="/import/exams"
        templateEndpoint="/import/template/exams"
        description='ייבוא מבחנים. עמודות חובה: קוד מבחן, שם מבחן, סוג, נושא, תאריך. שיוך לספרייה: חלק 1 + "מסימן 1 1" / "עד סימן 1 1" וכו׳ — עד 4 חלקים ו-4 רצפים לכל חלק. המועד נקבע לפי הבחירה למעלה.'
        onImportDone={() => loadData()}
        entityType="exams"
        identifierColumns={[
          { field: 'code', label: 'קוד מבחן' },
        ]}
        extraFormData={importExamPeriodId ? { periodId: importExamPeriodId } : undefined}
      />

      <Box sx={{ mt: 2, mb: 1, maxWidth: 280 }}>
        <TextField
          select
          size="small"
          fullWidth
          label="מועד לייבוא ציונים"
          value={importGradePeriodId}
          onChange={(e) => setImportGradePeriodId(e.target.value)}
        >
          {periods.map((p) => (
            <MenuItem key={p._id} value={p._id}>{p.name}{p.isActive ? ' (פעיל)' : ''}</MenuItem>
          ))}
        </TextField>
      </Box>

      <ImportSection
        label="ציונים"
        endpoint="/import/grades"
        templateEndpoint="/import/template/grades"
        description="עמודות: קוד אישי, קוד מבחן, ציון. המועד נקבע לפי הבחירה למעלה."
        entityType="grades"
        extraFormData={importGradePeriodId ? { periodId: importGradePeriodId } : undefined}
        periodName={periods.find((p) => p._id === importGradePeriodId)?.name}
        uploadDisabled={!importGradePeriodId}
        uploadDisabledReason="יש לבחור מועד לפני ייבוא ציונים"
      />
    </Box>
  );
};

export default ExamsList;
